/* v2 — includes scroll-progress + dot-nav (2026-07-02) */
/* =============================================================
   BABA — Global JS
   - Anchor injection (menu fragment URLs like /#extraction)
   - Reveal-on-scroll (.baba-reveal)
   ============================================================= */
(() => {
  if (typeof window === 'undefined') return;

  // -------------------------------------------------------------
  // Anchor injection
  // Sets scroll-margin-top on the section itself so fragment URLs
  // scroll to the right spot with proper header offset.
  // Also places a clean id on the section wrapper.
  // -------------------------------------------------------------
  const HEADER_OFFSET = 90;

  const anchorMap = [
    ['.baba-elixir',     'elixir'],
    ['.baba-extraction', 'extraction'],
    ['.baba-mushrooms',  'mushrooms'],
    ['.baba-farm',       'farm'],
    ['.baba-pharma',     'benefits'],
    ['.baba-ritual',     'ritual'],
    ['.baba-journal',    'journal']
  ];

  const injectAnchors = () => {
    anchorMap.forEach(([selector, id]) => {
      const target = document.querySelector(selector);
      if (!target) return;

      // Always apply scroll-margin-top on the section so a direct
      // scrollIntoView respects the fixed header.
      target.style.scrollMarginTop = HEADER_OFFSET + 'px';

      // Add the clean anchor id to the Shopify section wrapper
      // (the <div class="shopify-section" id="shopify-section-...">).
      // That element is free of conflicting ids on the inner section.
      const wrapper = target.closest('.shopify-section') || target.parentNode;
      if (wrapper && wrapper.id !== id) {
        // Make sure we don't duplicate
        const existing = document.getElementById(id);
        if (existing && existing !== wrapper) existing.removeAttribute('id');
        // If wrapper already has a shopify-generated id, move it to a data attr
        // and take over the friendly anchor id.
        if (wrapper.id && !wrapper.dataset.originalId) {
          wrapper.dataset.originalId = wrapper.id;
        }
        wrapper.id = id;
        wrapper.style.scrollMarginTop = HEADER_OFFSET + 'px';
      }
    });
  };

  const scrollToHash = (smooth = true) => {
    if (!location.hash || location.hash.length < 2) return;
    const el = document.querySelector(location.hash);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'start' });
    });
  };

  const run = () => {
    injectAnchors();
    scrollToHash(false); // instant on first load, avoid smooth-scroll interruption
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Final re-anchor once full page (images/video) finishes loading.
  window.addEventListener('load', () => {
    injectAnchors();
    // Only re-scroll if user hasn't navigated away from top
    if (window.scrollY < 50 && location.hash) scrollToHash(false);
  });

  // In-page fragment clicks (clicking Extraction from within the homepage)
  window.addEventListener('hashchange', () => scrollToHash(true));

  // Re-inject if Shopify hot-swaps a section in the customizer
  document.addEventListener('shopify:section:load', () => {
    injectAnchors();
  });

  // -------------------------------------------------------------
  // Reveal on scroll
  // -------------------------------------------------------------
  // In pinfade-deck mode the deck controller drives reveals per page.
  if (document.documentElement.classList.contains('pf-deck')) return;
  if (!('IntersectionObserver' in window)) return;

  const prefersReduced = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reveal = (el) => el.classList.add('in');

  if (prefersReduced) {
    document.querySelectorAll('.baba-reveal').forEach(reveal);
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        reveal(e.target);
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  const observeAll = (root = document) => {
    root.querySelectorAll('.baba-reveal:not(.in)').forEach((el) => io.observe(el));
  };
  observeAll();

  document.addEventListener('shopify:section:load', (e) => observeAll(e.target));
  document.addEventListener('shopify:section:select', (e) => observeAll(e.target));
})();

/* =============================================================
   Scroll progress bar + chapter dot-nav (added 2026-07-02)
   Self-contained; injects its own DOM. No layout edits needed.
   ============================================================= */
(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const build = () => {
    // The deck module owns the homepage nav in both modes — skip the scroll versions.
    if (document.documentElement.classList.contains('pf-deck') || document.documentElement.classList.contains('pf-mobile')) return;
    // --- top progress bar ---
    if (!document.getElementById('baba-progress')) {
      const bar = document.createElement('div');
      bar.id = 'baba-progress';
      document.body.appendChild(bar);
      const onScroll = () => {
        const h = document.documentElement;
        const max = (h.scrollHeight - h.clientHeight) || 1;
        bar.style.transform = 'scaleX(' + Math.min(1, (h.scrollTop || 0) / max) + ')';
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
    }
    // --- side chapter dot-nav ---
    const sections = Array.from(document.querySelectorAll('.baba-section'));
    if (sections.length && !document.getElementById('baba-dotnav')) {
      const nav = document.createElement('nav');
      nav.id = 'baba-dotnav';
      nav.setAttribute('aria-label', 'Section navigation');
      sections.forEach((sec, i) => {
        const dot = document.createElement('a');
        dot.className = 'baba-dot';
        const ch = sec.querySelector('.chapter');
        const h2 = sec.querySelector('h2, .baba-farm-heading, .baba-elixir-heading');
        const label = (ch && ch.textContent.trim()) || (h2 && h2.textContent.trim().slice(0, 22)) || ('Section ' + (i + 1));
        dot.setAttribute('data-label', label);
        dot.href = '#';
        dot.addEventListener('click', (e) => { e.preventDefault(); sec.scrollIntoView({ behavior: 'smooth', block: 'start' }); });
        nav.appendChild(dot);
        sec._babaDot = dot;
      });
      document.body.appendChild(nav);
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.target._babaDot) {
            nav.querySelectorAll('.baba-dot').forEach(d => d.classList.remove('active'));
            en.target._babaDot.classList.add('active');
          }
        });
      }, { threshold: 0.5 });
      sections.forEach(s => io.observe(s));
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build);
  else build();
  document.addEventListener('shopify:section:load', build);
})();

/* =============================================================
   PINFADE DECK  (approved mechanic — 2026-07-02)
   The homepage becomes a full-screen flick deck of the REAL
   sections: full-viewport pages, scroll-with-effort, then a
   smooth crossfade to the next page. Wheel · keys · swipe · dots.
   Revert: delete this block + the deck CSS block, or git checkout.
   ============================================================= */
(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Homepage only. Set class synchronously (pre-DOM) so the scroll
  // modules above bail and there's no flash of stacked sections.
  const ROOT = /^\/([a-z]{2}(-[a-z]{2})?\/?)?$/i;      // "/", "/fr", "/en-ca" …
  if (!ROOT.test(location.pathname)) return;
  // Desktop = flick deck (crossfade). Mobile = normal readable scroll + fade-in + dots
  // (the pin is a desktop-only delight; phones scroll naturally for readability).
  // HYBRID (2026-07-04): desktop = native scroll with SELECTED sections pinned full-screen
  // (pf-pin) and the rest flowing normally (pf-scroll). No scroll hijack, no fit-scaling —
  // so text is whatever we set and the original scroll-reveal animations just work.
  // Mobile = normal readable scroll (unchanged).
  const FLICK = window.matchMedia('(min-width: 801px)').matches;
  document.documentElement.classList.add(FLICK ? 'pf-hybrid' : 'pf-mobile');

  const SKIP = ['baba-press'];   // fake-press block stays out of the deck

  const start = () => {
    try {
      const main = document.querySelector('#MainContent') || document.querySelector('main') || document.body;
      const pages = Array.from(main.querySelectorAll('.shopify-section')).filter((w) => {
        const s = w.querySelector('.baba-hero, .baba-section');
        return s && !SKIP.some((c) => s.classList.contains(c));
      });
      if (pages.length < 2) { document.documentElement.classList.remove('pf-deck', 'pf-flick', 'pf-mobile', 'pf-hybrid'); return; }

      // ── HYBRID desktop: pin the chosen sections full-screen, scroll the rest.
      // Native scroll + the reveal-on-scroll modules above drive every animation —
      // no flick deck, no fit-scaling. Mobile is untouched (falls through below).
      if (document.documentElement.classList.contains('pf-hybrid')) {
        const PIN = ['baba-hero', 'baba-manifesto', 'baba-elixir', 'baba-extraction', 'baba-farm', 'baba-newsletter'];
        pages.forEach((w) => {
          const s = w.querySelector('.baba-hero, .baba-section');
          const isPin = !!(s && PIN.some((c) => s.classList.contains(c)));
          w.classList.add(isPin ? 'pf-pin' : 'pf-scroll');
          if (s) s.classList.add(isPin ? 'pf-pin' : 'pf-scroll');
        });
        return;   // native scroll + reveal-on-scroll take it from here
      }

      const html = document.documentElement, body = document.body, header = document.querySelector('.baba-header');
      if (FLICK) body.classList.add('pf-deck');
      pages.forEach((p) => p.classList.add('pf-page'));

      // progress bar
      let bar = document.getElementById('baba-progress');
      if (!bar) { bar = document.createElement('div'); bar.id = 'baba-progress'; body.appendChild(bar); }
      // section-index rail — always-visible titles + progress scrubber + click-to-jump
      ['baba-dotnav', 'baba-decknav'].forEach((id) => { const o = document.getElementById(id); if (o) o.remove(); });
      const deckNav = document.createElement('nav'); deckNav.id = 'baba-decknav'; deckNav.setAttribute('aria-label', 'Sections');
      const navProg = document.createElement('div'); navProg.className = 'dn-progress';
      const navFill = document.createElement('div'); navFill.className = 'dn-progress-fill'; navProg.appendChild(navFill);
      const navList = document.createElement('ul'); navList.className = 'dn-list';
      let jump = () => {};   // mode assigns this (flick: go · snap: scrollIntoView)
      const DARK = /baba-(hero|ritual|extraction|farm|newsletter)/;
      pages.forEach((p, i) => {
        const s = p.querySelector('.baba-hero, .baba-section');
        p._dark = DARK.test(s.className);
        const ch = s.querySelector('.chapter');
        let num = '', title = '';
        if (ch) {
          const sp = ch.querySelector('span');
          num = sp ? sp.textContent.trim() : '';
          const clone = ch.cloneNode(true); const cs = clone.querySelector('span'); if (cs) cs.remove();
          title = clone.textContent.trim().replace(/\s+/g, ' ');
        }
        if (!title) { const isFr = document.documentElement.lang.startsWith('fr'); title = (i === 0 ? (isFr ? 'Bienvenue' : 'Welcome') : 'Section ' + (i + 1)); }
        const li = document.createElement('li'); li.className = 'dn-item';
        const nEl = document.createElement('span'); nEl.className = 'dn-num'; nEl.textContent = num || '·';
        const tEl = document.createElement('span'); tEl.className = 'dn-title'; tEl.textContent = title;
        li.appendChild(nEl); li.appendChild(tEl);
        li.addEventListener('click', () => jump(i));
        navList.appendChild(li); p._nav = li;
      });
      deckNav.appendChild(navProg); deckNav.appendChild(navList); body.appendChild(deckNav);

      const revealsOf = (p) => Array.from(p.querySelectorAll('.baba-reveal'));
      const N = pages.length;

      // ── UNIFIED CASCADE ──────────────────────────────────────────────────
      // Every page's content materialises ONE ITEM AT A TIME, top-to-bottom, when
      // the page pins in (like the old scroll version). Repeating items (benefit
      // rows, ritual cards, tiles, mushroom columns, farm steps) cascade individually
      // instead of their container appearing as one block.
      const ITEM_SEL = '.baba-elixir-facts li, .baba-pharma-row, .baba-ritual-card, .baba-eb, .baba-mush-card, .baba-pstep';
      const unitsOf = (p) => {
        const units = [];
        revealsOf(p).forEach((rev) => {
          const items = rev.querySelectorAll(ITEM_SEL);
          if (items.length) items.forEach((it) => units.push(it));   // swap block → its items
          else units.push(rev);
        });
        p.querySelectorAll(ITEM_SEL).forEach((it) => { if (units.indexOf(it) < 0 && !units.some((u) => u !== it && u.contains(it))) units.push(it); });
        units.sort((a, b) => { const ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect(); return (ra.top - rb.top) || (ra.left - rb.left); });
        return units;
      };
      const setImp = (el, o, t, tr, d) => {
        el.style.setProperty('opacity', o, 'important');
        el.style.setProperty('transform', t, 'important');
        el.style.setProperty('transition', tr, 'important');
        el.style.setProperty('transition-delay', d, 'important');
      };
      const cascadeReset = (p) => unitsOf(p).forEach((u) => { u.classList.remove('in'); setImp(u, '0', 'translateY(30px)', 'none', '0s'); });
      const cascadeIn = (p) => unitsOf(p).forEach((u, i) => { u.classList.add('in'); setImp(u, '1', 'none', 'opacity 1.15s var(--ease), transform 1.15s var(--ease)', (0.18 + i * 0.14).toFixed(2) + 's'); });
      // ─────────────────────────────────────────────────────────────────────

      // shared: light up the current section in the rail + progress
      const setActive = (i) => {
        pages.forEach((p, k) => { if (p._nav) p._nav.classList.toggle('active', k === i); });
        if (bar) bar.style.transform = 'scaleX(' + (N > 1 ? i / (N - 1) : 1) + ')';
        if (navFill) navFill.style.height = (N > 1 ? (i / (N - 1)) * 100 : 100) + '%';
        deckNav.classList.toggle('on-dark', !!(pages[i] && pages[i]._dark));
      };

      if (FLICK) {
        /* ═══════════ DESKTOP — FLICK DECK (hijack scroll, crossfade) ═══════════ */
        pages.forEach((p) => cascadeReset(p));   // hide every page's items until it pins in

        const fit = (p) => {
          const s = p.querySelector('.baba-hero, .baba-section'); if (!s || s.classList.contains('baba-hero')) return;
          // full-bleed sections (extraction) keep their background edge-to-edge —
          // only their inner content is scaled, never the whole section.
          const c = s.querySelector('.baba-wrap, .baba-wrap-narrow, .baba-extraction-inner') || s;
          c.classList.add('pf-fit'); c.style.transform = 'none';
          // GROW-ON-FLIP FIX: measure the SETTLED layout. Reveal elements start at
          // translateY(...) (inflates scrollHeight) and the incoming page is mid flip-zoom
          // (transform on .pf-page inflates the measured rect). Both make fit() compute a
          // too-small scale, which then "grows" ~5% once things settle. Neutralize both for
          // the measurement ONLY (synchronous — no paint between set/restore, so no flash).
          const reveals = c.querySelectorAll('.baba-reveal, ' + ITEM_SEL);
          const saved = [];
          reveals.forEach((r) => { saved.push([r, r.style.getPropertyValue('transform'), r.style.getPropertyValue('transition'), r.style.getPropertyPriority('transform')]); r.style.setProperty('transition', 'none', 'important'); r.style.setProperty('transform', 'none', 'important'); });
          const pTf = p.style.transform, pTr = p.style.transition;
          p.style.transition = 'none'; p.style.transform = 'none';
          const cs = getComputedStyle(s);
          const availH = s.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
          const availW = s.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
          let sc = Math.min(1, (availH * 0.99) / (c.scrollHeight || 1), availW / (c.scrollWidth || 1));
          c.style.transform = sc < 0.996 ? 'scale(' + sc.toFixed(3) + ')' : 'none';
          for (let k = 0; k < 3; k++) {
            const r = c.getBoundingClientRect();
            if (r.height <= availH + 2 || sc <= 0.05) break;
            sc = sc * (availH / r.height) * 0.99;
            c.style.transform = 'scale(' + sc.toFixed(3) + ')';
          }
          p.style.transform = pTf; p.style.transition = pTr;
          saved.forEach(([r, tf, tr, pr]) => { r.style.setProperty('transform', tf, pr); r.style.setProperty('transition', tr, pr); });
        };
        const fitAll = () => pages.forEach(fit);

        let cur = 0, locked = false, acc = 0;
        const THRESH = 60, LOCK = 1050;

        const render = () => {
          pages.forEach((p, i) => {
            const on = i === cur;
            p.classList.toggle('pf-on', on);
            p.querySelectorAll('[data-baba-ritual-card]').forEach((c) => c.classList.toggle('is-drawn', on));
          });
          // Slow-dance cascade: the active page's items materialise one-by-one, top-to-bottom.
          // rAF so the page is laid out (visible) before we measure order + start the reveal.
          requestAnimationFrame(() => cascadeIn(pages[cur]));
          setActive(cur);
          if (header) header.classList.toggle('scrolled', cur > 0);  // solid header off the dark hero
        };

        const go = (i, force) => {
          if (locked && !force) return;   // wheel/touch obey the lock; menu jumps (force) don't
          i = Math.max(0, Math.min(N - 1, i));
          if (i === cur) { acc = 0; return; }
          const from = cur;
          locked = true; cur = i; fit(pages[cur]); render();
          // Outgoing page dissolves away ON TOP of the solid incoming (single-layer, no seam).
          pages.forEach((p) => { if (p !== pages[from]) p.classList.remove('pf-leaving'); });
          pages[from].classList.add('pf-leaving');
          setTimeout(() => { pages[from].classList.remove('pf-leaving'); cascadeReset(pages[from]); }, 1200);
          setTimeout(() => { locked = false; acc = 0; }, LOCK);
        };
        jump = go;
        window.__deckGo = go; window.__deckCur = () => cur; window.__deckN = () => N;

        const inScroller = (t) => t && t.closest && t.closest('cart-drawer, .drawer, .baba-drawer, .cart-drawer');

        window.addEventListener('wheel', (e) => {
          if (inScroller(e.target)) return;
          e.preventDefault();
          if (locked) return;
          acc += e.deltaY;
          if (acc > THRESH) go(cur + 1);
          else if (acc < -THRESH) go(cur - 1);
        }, { passive: false });

        window.addEventListener('keydown', (e) => {
          if (['ArrowDown', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); go(cur + 1); }
          else if (['ArrowUp', 'PageUp'].includes(e.key)) { e.preventDefault(); go(cur - 1); }
          else if (e.key === 'Home') { e.preventDefault(); go(0); }
          else if (e.key === 'End') { e.preventDefault(); go(N - 1); }
        });

        let ty = null;
        window.addEventListener('touchstart', (e) => { ty = e.touches[0].clientY; }, { passive: true });
        window.addEventListener('touchmove', (e) => { if (!inScroller(e.target)) e.preventDefault(); }, { passive: false });
        window.addEventListener('touchend', (e) => {
          if (ty == null) return;
          const dy = ty - e.changedTouches[0].clientY;
          if (Math.abs(dy) > 45) go(cur + (dy > 0 ? 1 : -1));
          ty = null;
        }, { passive: true });

        const hashJump = () => {
          const id = (location.hash || '').slice(1); if (!id) return;
          const idx = pages.findIndex((p) => (p.id === id) || p.querySelector('#' + CSS.escape(id) + ', .baba-' + id));
          if (idx >= 0) go(idx, true);
        };
        window.addEventListener('hashchange', hashJump);
        window.addEventListener('resize', fitAll);
        window.addEventListener('load', () => { fitAll(); render(); hashJump(); });

        const refit = () => { fitAll(); render(); };
        main.querySelectorAll('img').forEach((img) => { if (!img.complete) img.addEventListener('load', refit, { once: true }); });
        main.querySelectorAll('video').forEach((v) => v.addEventListener('loadeddata', refit, { once: true }));

        fitAll(); render();
        setTimeout(fitAll, 900);
        setTimeout(() => { fitAll(); render(); hashJump(); }, 1800);
      } else {
        /* ═══════════ MOBILE — NATIVE SNAP SCROLL + FADE-IN ═══════════ */
        jump = (i) => { const p = pages[i]; if (p) p.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
        window.__deckGo = jump; window.__deckCur = () => 0; window.__deckN = () => N;

        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => { if (e.isIntersecting) revealsOf(e.target).forEach((r) => r.classList.add('in')); });
          // active = the page occupying the most of the viewport
          let best = 0, bestVis = -1;
          pages.forEach((p, k) => {
            const r = p.getBoundingClientRect();
            const vis = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
            if (vis > bestVis) { bestVis = vis; best = k; }
          });
          setActive(best);
        }, { threshold: [0, 0.2, 0.5, 0.8, 1] });
        pages.forEach((p) => io.observe(p));
        setActive(0);
      }

      // ── Menu / anchor clicks → jump straight to the section ──
      // Don't rely on `hashchange`: it never fires when the URL hash already
      // equals the link target, which happens constantly because the deck does
      // NOT rewrite the hash as you scroll. So clicking e.g. "Extraction" after
      // scrolling away was a dead click. Intercept the click, resolve the target
      // page and jump directly (forced past the transition lock).
      document.addEventListener('click', (e) => {
        const a = e.target && e.target.closest && e.target.closest('a[href*="#"]');
        if (!a) return;
        const href = a.getAttribute('href') || '';
        const hi = href.indexOf('#'); if (hi < 0) return;
        const frag = href.slice(hi + 1); if (!frag) return;
        const path = href.slice(0, hi);
        if (path && path !== '/' && path !== location.pathname) return; // same-page fragments only
        const idx = pages.findIndex((p) => (p.id === frag) || p.querySelector('#' + CSS.escape(frag) + ', .baba-' + frag));
        if (idx < 0) return;
        e.preventDefault();
        if (typeof window.__deckGo === 'function') window.__deckGo(idx, true);
        try { history.replaceState(null, '', '#' + frag); } catch (_) {}
      }, true);
    } catch (err) {
      // never trap the page — fall back to normal scroll
      document.documentElement.classList.remove('pf-deck', 'pf-flick', 'pf-mobile');
      document.body && document.body.classList.remove('pf-deck');
      console && console.warn && console.warn('[pinfade-deck] disabled:', err && err.message);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();